import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Card, Grid, Box, IconButton, Divider, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Modal, Fade, Backdrop, Snackbar, Icon } from "@mui/material";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import DescriptionIcon from "@mui/icons-material/Description";
import MDBoxRoot from "components/MDBox/MDBoxRoot";

function LessonsDetails() {
  const { lesson_id } = useParams();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewPdfUrl, setViewPdfUrl] = useState(null);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });
  const handleOpenImage = (img) => setSelectedImage(img);
  const handleCloseImage = () => setSelectedImage(null);

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  // Fetch lesson details
  const fetchLessonDetails = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/lessons/details/${lesson_id}`, { withCredentials: true });
    return response.data;
  };

  const { data: lessonResponse, isLoading, isError } = useQuery({
    queryKey: ["lessonDetails", lesson_id],
    queryFn: fetchLessonDetails,
  });

  const lesson = lessonResponse?.data;
  const isAuthor = String(meData?.user_id) === String(lesson?.user_id);

  const handleEdit = () => {
    navigate(`/lessons/edit/${lesson_id}`);
  };

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.toLowerCase();
    const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");

    if (isWord) {
      // Use Office Online Viewer
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&wdStartOnLook=1`;
      setViewPdfUrl(officeUrl);
    } else {
      setViewPdfUrl(url);
    }
  };

  const handleDownload = async (url, title) => {
    if (!url) return;
    try {
      const cleanUrl = url.split('?')[0].toLowerCase();
      const originalExt = cleanUrl.split('.').pop();

      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD')}.${originalExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({ open: true, message: "Download started!", severity: "success" });
    } catch (error) {
      console.error("Download failed", error);
      setSnackbar({ open: true, message: "Download failed.", severity: "error" });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setLoading(true);
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/lessons/delete/${lesson_id}`, { withCredentials: true });
      setSnackbar({
        open: true,
        message: response?.data?.message || "Lesson deleted successfully!",
        severity: "success"
      });
      setTimeout(() => {
        navigate("/lessons/list");
      }, 2000);
    } catch (error) {
      console.error("DEBUG: Delete fail:", error.response || error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to delete lesson.",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
      setLoading(true); // Keep loading during navigation
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isError || !lesson) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <Alert severity="error">Failed to load lesson details.</Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox py={3}>
        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center" gap={2}>
            
            <MDTypography variant="h4" fontWeight="bold">Lesson Details</MDTypography>
          </MDBox>

                     <Box display="flex" gap={2}>

          {isAuthor && (
            <>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleEdit}
                sx={{ px: 3 }}
              >
                <EditIcon sx={{ mr: 1 }} /> Edit Lesson
              </MDButton>
              <MDButton
                variant="gradient"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ px: 3 }}
              >
                <DeleteIcon sx={{ mr: 1 }} /> Delete Post
              </MDButton>
            </>
          )}
           <MDButton
              variant="outlined"
              color="info"
              onClick={() => navigate("/lessons/list")}
              sx={{ px: 2, borderRadius: "10px" }}
            >
              <Icon sx={{ mr: 1, fontWeight: "bold" }}>arrow_back</Icon> Back to Lessons
            </MDButton>
            </Box>
        </MDBox>

        <Card sx={{ overflow: "visible" }}>
          <MDBox p={4}>
            <MDTypography variant="h3" fontWeight="bold" mb={1} color="dark">
              {lesson.title || "Lesson Details"}
            </MDTypography>

            <MDBox display="flex" alignItems="center" gap={3} mb={3}>
              <MDTypography variant="button" fontWeight="medium" color="text">
                By {lesson.user_name || "Unknown User"}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={0.5}>
                <AccessTimeIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                <MDTypography variant="caption" color="text">
                  {moment(lesson.created_at).format("DD MMM YYYY")}
                </MDTypography>
              </MDBox>
            </MDBox>

            <Divider />

            {/* Content section */}
            <MDBox mt={3} mb={5}>
              <Box
                className="quill-content"
                dangerouslySetInnerHTML={{ __html: lesson.content }}
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  "& p": { mb: 2 },
                  "& ul, & ol": { ml: 4, mb: 2 }
                }}
              />
            </MDBox>

            {/* Photos section */}
            {lesson.photos?.length > 0 && (
              <MDBox mt={5}>
                <Divider />
                <MDTypography variant="h5" fontWeight="bold" mt={3} mb={3}>
                  Photos
                </MDTypography>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: { xs: 'wrap', md: 'nowrap' }, // Wrap on mobile
                  overflowX: { xs: 'visible', md: 'auto' },
                  pb: 2,
                  '&::-webkit-scrollbar': { height: '8px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '4px' }
                }}>
                  {lesson.photos.map((photo, idx) => (
                    <Box
                      key={`photo-${idx}`}
                      onClick={() => handleOpenImage(photo)}
                      sx={{
                        minWidth: { xs: 'calc(50% - 10px)', md: 280 }, // 2 per row on mobile
                        height: { xs: 120, md: 200 },
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 2,
                        cursor: 'pointer',
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.02)' }
                      }}
                    >
                      <img src={photo} alt="Lesson" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                  ))}
                </Box>
              </MDBox>
            )}

            {/* Videos section */}
            {(lesson.video_url || lesson.videos) && (
              <MDBox mt={4}>
                <Divider />
                <MDTypography variant="h5" fontWeight="bold" mt={3} mb={3}>
                  Videos
                </MDTypography>
                <Grid container spacing={2}>
                  {(() => {
                    const videoData = lesson.video_url || lesson.videos;
                    if (!videoData) return [];
                    if (Array.isArray(videoData)) return videoData;
                    try {
                      const parsed = JSON.parse(videoData);
                      if (Array.isArray(parsed)) return parsed;
                    } catch (e) {
                      // Fallback
                    }
                    if (typeof videoData === "string") {
                      return videoData.split(",").map(v => v.trim()).filter(v => v !== "");
                    }
                    return [];
                  })().map((video, idx) => (
                    <Grid item xs={12} key={`video-${idx}`}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "#e3f2fd",
                          border: "1px solid #90caf9",
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <MDTypography variant="button" fontWeight="bold" color="info">
                          Video Link {idx + 1}:
                        </MDTypography>
                        <MDTypography
                          component="a"
                          href={video}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="button"
                          color="info"
                          sx={{
                            fontWeight: "bold",
                            textDecoration: "underline",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {video}
                        </MDTypography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            )}

            {/* PDFs and Documents section */}
            {lesson.pdfs?.length > 0 && (
              <MDBox mt={4}>
                <Divider />
                <MDTypography variant="h5" fontWeight="bold" mt={3} mb={3}>
                  Documents (PDFs & Word)
                </MDTypography>
                <Grid container spacing={3}>
                  {lesson.pdfs.map((pdf, idx) => {
                    const isWord = pdf.toLowerCase().split('?')[0].endsWith(".doc") || pdf.toLowerCase().split('?')[0].endsWith(".docx");
                    return (
                      <Grid item xs={12} md={6} key={`pdf-${idx}`}>
                        <Box sx={{ border: "1px solid #eee", borderRadius: 3, bgcolor: "#fcfcfc", boxShadow: 1, overflow: 'hidden' }}>
                          <Box p={2} display="flex" justifyContent="space-between" alignItems="center" bgcolor="#f8f9fa" borderBottom="1px solid #eee">
                            <MDTypography variant="button" fontWeight="bold">
                              {isWord ? "Word Document" : "PDF Document"} {idx + 1}
                            </MDTypography>
                            <MDButton
                              variant="gradient"
                              color="success"
                              size="small"
                              onClick={() => {
                                handlePreview(pdf);
                                // Also trigger download in background or just open preview
                                // The user asked for Download / Open button logic
                              }}
                              sx={{
                                height: 32,
                                background: 'linear-gradient(195deg, #66bb6a, #43a047)',
                                color: '#ffffff',
                                '&:hover': {
                                  background: 'linear-gradient(195deg, #43a047, #2e7d32)',
                                }
                              }}
                            >
                              DOWNLOAD / OPEN
                            </MDButton>
                          </Box>
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            {isWord ? (
                              <Box
                                onClick={() => handlePreview(pdf)}
                                sx={{
                                  p: 4,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#f0f0f0' },
                                  borderRadius: 2,
                                  border: '1px dashed #ccc',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <DescriptionIcon sx={{ fontSize: 60, color: '#2196f3' }} />
                                <MDTypography variant="caption" fontWeight="bold">Click to Preview Word Document</MDTypography>
                              </Box>
                            ) : (
                              <iframe src={pdf} width="100%" height="450px" style={{ border: "none", borderRadius: '8px' }} title={`PDF ${idx}`} />
                            )}
                          </Box>
                          <Box p={1} display="flex" justifyContent="center">
                            <MDButton
                              size="small"
                              variant="text"
                              color="info"
                              onClick={() => handleDownload(pdf, isWord ? `Word_Doc_${idx + 1}` : `PDF_Doc_${idx + 1}`)}
                            >
                              Click here to download directly
                            </MDButton>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Lightbox Modal */}
      <Modal
        open={Boolean(selectedImage)}
        onClose={handleCloseImage}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500, sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
      >
        <Fade in={Boolean(selectedImage)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <IconButton
              onClick={handleCloseImage}
              sx={{
                position: 'fixed', // Fixed to stay visible
                top: 20,
                right: 20,
                color: 'white',
                bgcolor: 'red !important',
                zIndex: 1400, // Above modal
                '&:hover': { bgcolor: '#d32f2f !important' }
              }}
            >
              <CloseIcon sx={{ fontSize: 35 }} />
            </IconButton>
            <img src={selectedImage} alt="Fullscreen" style={{ maxWidth: '100%', maxHeight: '95vh', borderRadius: 8, boxShadow: '0 0 20px rgba(0,0,0,0.8)', display: 'block' }} />
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <DeleteIcon color="error" sx={{ fontSize: 50, mb: 1 }} />
          <MDTypography variant="h5" fontWeight="bold">Delete Post?</MDTypography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <MDTypography variant="body2" color="text">
            Are you sure you want to remove this lesson? <br />
            This action is permanent and cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color="light" sx={{ minWidth: 100 }}>
            No, Cancel
          </MDButton>
          <MDButton onClick={handleDelete} color="error" variant="gradient" disabled={isDeleting} sx={{ minWidth: 100 }}>
            {isDeleting ? "Deleting..." : "Yes, Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={Boolean(viewPdfUrl)} onClose={() => setViewPdfUrl(null)} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          Document Viewer
          <IconButton onClick={() => setViewPdfUrl(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '80vh', p: 0 }}>
          {viewPdfUrl && (
            <iframe
              src={viewPdfUrl}
              width="100%"
              height="700px"
              style={{ border: 'none' }}
              title="Document Preview"
            />
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setViewPdfUrl(null)} color="dark">Close</MDButton>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', color: 'white !important' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default LessonsDetails;
